<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import {
  maintenanceBanners, maintenanceNow, dismissMaintenance,
} from '@/helpers/maintenance';
import { updateBannerVisible } from '@/helpers/bannerState';
import {
  windowPhase, impactMeta, kindMeta, formatDateTime, relativeText, scopeText,
} from '@/helpers/maintenanceLogic';

// Meldung oben in der Anwendung (wie der Update-Hinweis): ein Wartungsfenster läuft oder beginnt innerhalb der
// Vorlaufzeit (Standard 24 h). Weggeklickt wird pro Fenster und Phase im Browser gemerkt (maintenance.js).
const MAX_VISIBLE = 3;

const store = useStore();
const authenticated = computed(() => Boolean(store.getters['auth/isApiAuthenticated']));

const entries = computed(() => maintenanceBanners.value.map((w) => {
  const phase = windowPhase(w, maintenanceNow.value);
  const impact = impactMeta(w.impact);
  return {
    w,
    phase,
    color: impact.color,
    icon: kindMeta(w.kind).icon,
    label: phase === 'active' ? 'Wartung läuft' : (w.kind === 'emergency' ? 'Notfall-Wartung geplant' : 'Wartung geplant'),
    detail: phase === 'active'
      ? `bis ${formatDateTime(w.endsAt)}`
      : `${relativeText(w, maintenanceNow.value).replace('Beginnt', 'beginnt')} (${formatDateTime(w.startsAt)})`,
    scope: scopeText(w),
    impact: impact.short,
  };
}));

const shown = computed(() => entries.value.slice(0, MAX_VISIBLE));
const hidden = computed(() => Math.max(0, entries.value.length - MAX_VISIBLE));
</script>

<template>
  <div
    v-if="authenticated && entries.length"
    class="app-maintenance-banners"
    :class="{ 'app-maintenance-banners--below-update': updateBannerVisible }"
    data-test="maintenance-banners"
  >
    <div
      v-for="entry in shown"
      :key="`${entry.w.id}-${entry.phase}`"
      class="app-maintenance-banner shadow-sm"
      :class="`app-maintenance-banner--${entry.color}`"
      role="alert"
      data-test="maintenance-banner"
    >
      <i class="me-2 fs-5" :class="entry.icon"></i>
      <span class="app-maintenance-banner__text">
        <strong>{{ entry.label }}:</strong> {{ entry.w.title }}
        <span class="app-maintenance-banner__detail">· {{ entry.detail }} · {{ entry.scope }} · {{ entry.impact }}</span>
      </span>
      <router-link to="/" class="btn btn-sm btn-light fw-semibold ms-3 flex-shrink-0">Details</router-link>
      <button
        type="button"
        class="btn-close ms-3 flex-shrink-0"
        :class="entry.color === 'warning' ? '' : 'btn-close-white'"
        aria-label="Ausblenden"
        title="Ausblenden"
        @click="dismissMaintenance(entry.w)"
      ></button>
    </div>
    <router-link v-if="hidden" to="/" class="app-maintenance-more">+ {{ hidden }} weitere Wartungsfenster</router-link>
  </div>
</template>

<style scoped>
.app-maintenance-banners {
  position: fixed;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  /* Unter Dialogen (Bootstrap-Modal 1055, Backdrop 1050), aber über Kopfzeile und Menü */
  z-index: 1040;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  width: max-content;
  max-width: min(94vw, 62rem);
  margin-top: 0.5rem;
}
/* Der Update-Hinweis belegt dieselbe Stelle; die Wartungsmeldungen rücken darunter. */
.app-maintenance-banners--below-update { margin-top: 3.6rem; }
.app-maintenance-banner {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  color: #fff;
  font-size: 0.9rem;
}
.app-maintenance-banner--info { background: var(--vz-info, #299cdb); }
.app-maintenance-banner--warning { background: var(--vz-warning, #f7b84b); color: #1f2937; }
.app-maintenance-banner--danger { background: var(--vz-danger, #f06548); }
.app-maintenance-banner__text { min-width: 0; }
.app-maintenance-banner__detail { opacity: 0.92; }
.app-maintenance-more {
  padding: 0.15rem 0.7rem;
  border-radius: 1rem;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 0.8rem;
}
</style>
